import { handlePageNumberInPagination } from 'common/pagination';
import { UserDetailsContext } from 'context/userDetailsContext';
import moment from 'moment';
import { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getCaseList } from 'services/case/caseServices';
import { convertToTimezoneFormat } from 'utils/utility_functions/timezone';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../config/queryClient';

export function useCaseList(_filter, _pageSize = 10, options = {}) {
  const { isCase = false, isLeads = false } = options;
  const userContext = useContext(UserDetailsContext);
  const queryClient = useQueryClient();

  const [isForward, setIsForward] = useState(true);
  const [pageSize, setpageSize] = useState(userContext?.firmDetails?.pagination_preference?.case?.self ?? _pageSize);
  const [paginationParams, setPaginationParams] = useState({
    pagination_token: '',
    pagination_direction: 'forward',
  });

  useEffect(() => {
    setpageSize(userContext?.firmDetails?.pagination_preference?.case?.self);
  }, [userContext]);

  const pickExtractedDate = useCallback(
    (dateArray) => {
      if (!dateArray) {
        return undefined;
      }
      return {
        from: dateArray?.[0] ? convertToTimezoneFormat(moment(dateArray[0]).startOf('day'), userContext?.userDetails.timezone) : '',
        to: dateArray?.[0]
          ? convertToTimezoneFormat(moment(dateArray[1] ? dateArray[1] : dateArray[0]).endOf('day'), userContext?.userDetails.timezone)
          : '',
      };
    },
    [userContext?.userDetails?.timezone]
  );

  // Build query key with all parameters for proper caching
  const queryKey = useMemo(() => {
    const filterKey = {
      ..._filter,
      isCase,
      isLeads,
    };
    return queryKeys.caseList.list(filterKey, {
      limit: pageSize,
      ...paginationParams,
    });
  }, [_filter, pageSize, paginationParams, isCase, isLeads]);

  // Build request payload
  const buildRequestPayload = useCallback(
    (filter, limit, pagination_token, pagination_direction) => {
      return {
        data: {
          ...filter,
          statute_of_limitations: undefined,
          date_created: undefined,
          date_closed: undefined,
          last_updated: undefined,
          case_dates_filter: {
            statute_of_limitations: filter?.statute_of_limitations
              ? convertToTimezoneFormat(moment(filter?.statute_of_limitations), userContext?.userDetails.timezone)
              : '',
            date_created: pickExtractedDate(filter?.date_created),
            date_closed: pickExtractedDate(filter?.date_closed),
            last_updated: pickExtractedDate(filter?.last_updated),
          },
        },
        limit,
        pagination_token,
        pagination_direction,
        searchValue: filter?.keyword,
        caseMatter: filter?.case_matter,
      };
    },
    [userContext?.userDetails?.timezone, pickExtractedDate]
  );

  // React Query hook
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const payload = buildRequestPayload(_filter, pageSize, paginationParams.pagination_token, paginationParams.pagination_direction);
      const response = await getCaseList(payload);
      return response?.data;
    },
    staleTime: isCase || isLeads ? 5 * 60 * 1000 : 2 * 60 * 1000, // 5 minutes for cases/leads, 2 minutes otherwise
    gcTime: isCase || isLeads ? 30 * 60 * 1000 : 10 * 60 * 1000, // 30 minutes for cases/leads, 10 minutes otherwise
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: true,
  });

  const next_Token = data?.next_token;
  const transition_Token = data?.transition_token;
  const caseList = data?.['case'] || [];

  const hasNext = next_Token === null && isForward;
  const hasPrevious = transition_Token === null || (transition_Token && next_Token === null && !isForward);

  const fetchNext = useCallback(() => {
    setIsForward(true);
    setPaginationParams({
      pagination_token: !isForward ? transition_Token : next_Token,
      pagination_direction: 'forward',
    });
  }, [isForward, transition_Token, next_Token]);

  const fetchprev = useCallback(() => {
    setIsForward(false);
    setPaginationParams({
      pagination_token: isForward ? transition_Token : next_Token,
      pagination_direction: 'reverse',
    });
  }, [isForward, transition_Token, next_Token]);

  const onPageChange = useCallback(
    (e) => {
      setpageSize(e.value);
      handlePageNumberInPagination(userContext, 'case', '', 'self', e.value);
      setPaginationParams({
        pagination_token: '',
        pagination_direction: 'forward',
      });
      setIsForward(true);
    },
    [userContext]
  );

  const loadCaseList = useCallback(
    ({ limit = pageSize, pagination_token = '', pagination_direction = 'forward', filter = _filter }) => {
      setPaginationParams({
        pagination_token,
        pagination_direction,
      });
      if (limit !== pageSize) {
        setpageSize(limit);
      }
      // Invalidate and refetch with new params
      const newFilterKey = {
        ...filter,
        isCase,
        isLeads,
      };
      const newQueryKey = queryKeys.caseList.list(newFilterKey, {
        limit,
        pagination_token,
        pagination_direction,
      });
      queryClient.invalidateQueries({ queryKey: newQueryKey });
    },
    [pageSize, _filter, isCase, isLeads, queryClient]
  );

  return { caseList, hasNext, hasPrevious, pageSize, fetchNext, fetchprev, onPageChange, loadCaseList, isLoading, error };
}
